import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Select } from '@mantine/core';
import AppStack from '../../common/AppStack';
import { singularPluralFormat } from '../../../helpers/format';
import { ECOM_PRODUCT_STATUS_ENUM } from '../../../config/constants';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import AppText from '../../common/AppText';
import { triggerNotification } from '../../../helpers/notification';
import ResourceSelectModal from '../../common/ResourceSelectModal';

const PRODUCT_STATUS_OPTIONS = [
  {
    value: ECOM_PRODUCT_STATUS_ENUM.ACTIVE,
    label: 'Active'
  },
  {
    value: ECOM_PRODUCT_STATUS_ENUM.DRAFT,
    label: 'Draft'
  }
];

const ChangeProductStatusModal = ({
  isOpen,
  onClose,
  fkEcomVendor,
  fkEcomStore,
  archiveProducts,
  selectFirstItem,
  selectedProductIds,
  filter,
  totalItemCount,
  selectAllDisabled,
  onSuccess
}) => {
  const [formState, setFormState] = useState({
    selectType: '',
    selectedOptionValue: '',
    loading: false
  });
  const {
    state: vendorState,
    fetchVendorProductOptionsSummary,
    updateVendorProducts
  } = useContext(VendorContext);
  const {
    state: storeState,
    fetchStoreProductOptionsSummary,
    updateStoreProducts
  } = useContext(StoreContext);
  const selectedOption = PRODUCT_STATUS_OPTIONS.find(
    (o) => o.value === formState.selectedOptionValue
  );
  const productIds =
    (formState.selectType === 'all'
      ? fkEcomVendor
        ? vendorState.ecomVendorProductSummaryOptions.value?.ecomVendorProducts.map(
            (p) => p.uuid
          )
        : storeState.ecomStoreProductSummaryOptions.value?.ecomStoreProducts.map(
            (p) => p.uuid
          )
      : selectedProductIds) || [];

  useEffect(() => {
    if (isOpen) {
      setFormState({
        selectType: '',
        selectedOptionValue: PRODUCT_STATUS_OPTIONS[0]?.value,
        loading: false
      });
    }
  }, [isOpen]);

  const onSuccessCallback = () => {
    triggerNotification(`Product status updated.`, 'success');
    onClose();
    if (onSuccess) {
      onSuccess();
    }
  };

  const onErrorCallback = (e) => {
    setFormState({ ...formState, loading: false });
    triggerNotification(e);
  };

  return (
    <ResourceSelectModal
      filter={filter}
      formSectionProps={{
        onCancel: onClose,
        isLoading: formState.loading,
        onSubmit: () => {
          setFormState({ ...formState, loading: true });
          if (fkEcomVendor) {
            updateVendorProducts(
              fkEcomVendor,
              {
                ecomVendorProducts: productIds.map((uuid) => ({
                  uuid,
                  fkEcomProductStatus: archiveProducts
                    ? ECOM_PRODUCT_STATUS_ENUM.ARCHIVED
                    : selectedOption?.value
                })),
                selectFirstItem,
                skipResponse: !selectFirstItem
              },
              onSuccessCallback,
              onErrorCallback
            );
          }
          else {
            updateStoreProducts(
              fkEcomStore,
              {
                ecomStoreProducts: productIds.map((uuid) => ({
                  uuid,
                  fkEcomProductStatus: archiveProducts
                    ? ECOM_PRODUCT_STATUS_ENUM.ARCHIVED
                    : selectedOption?.value
                })),
                selectFirstItem,
                skipResponse: !selectFirstItem
              },
              onSuccessCallback,
              onErrorCallback
            );
          }
        },
        submitTitle: archiveProducts
          ? 'Yes, archive products'
          : `Set as ${selectedOption?.label.toLowerCase() ?? ''}`
      }}
      isLoading={
        storeState.ecomStoreProductSummaryOptions.loading ||
        vendorState.ecomVendorProductSummaryOptions.loading
      }
      isOpen={isOpen}
      onClose={onClose}
      onSelect={(selectType) => {
        setFormState({ ...formState, selectType });
        if (selectType === 'all') {
          if (fkEcomVendor) {
            fetchVendorProductOptionsSummary(
              fkEcomVendor,
              filter,
              null,
              triggerNotification
            );
          }
          else {
            fetchStoreProductOptionsSummary(
              fkEcomStore,
              filter,
              null,
              triggerNotification
            );
          }
        }
      }}
      selectedIds={selectedProductIds}
      selectLabel={archiveProducts ? 'Archive for' : 'Change status for'}
      selectLabelDescription={
        archiveProducts
          ? 'Select the products you want to archive.'
          : 'Select the products you want to change the status for.'
      }
      size={450}
      skipSelect={selectFirstItem || selectAllDisabled}
      title={`${
        archiveProducts ? 'Archive products?' : 'Change product status'
      }`}
      totalItemCount={totalItemCount}
    >
      <AppStack style={{ gap: 8 }}>
        {!selectFirstItem && (
          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            {archiveProducts ? 'Archiving' : 'Setting status for'}{' '}
            {singularPluralFormat(
              formState.selectType === 'all'
                ? storeState.ecomStoreProductSummaryOptions.value
                    ?.ecomStoreProducts.length
                : selectedProductIds?.length,
              'product',
              'products'
            )}
            .
          </AppText>
        )}

        <AppStack style={{ gap: 20 }}>
          {!archiveProducts && (
            <Select
              data={PRODUCT_STATUS_OPTIONS}
              disabled={formState.loading}
              label="New status"
              onChange={(v) => {
                if (v) {
                  setFormState({
                    ...formState,
                    selectedOptionValue: v
                  });
                }
              }}
              required
              searchable
              value={formState.selectedOptionValue}
            />
          )}

          <Alert color="blue" title="" variant="outline">
            <AppStack style={{ gap: 10 }}>
              {archiveProducts ? (
                <AppText style={{ fontSize: 14 }}>
                  Are you sure you want to archive{' '}
                  <b>
                    {singularPluralFormat(
                      productIds.length,
                      'product',
                      'products'
                    )}
                  </b>
                  ?
                </AppText>
              ) : (
                <AppText style={{ fontSize: 14 }}>
                  Are you sure you want to <b>change the status</b> of{' '}
                  <b>
                    {singularPluralFormat(
                      productIds.length,
                      'product',
                      'products'
                    )}
                  </b>{' '}
                  to <b>{selectedOption?.label ?? ''}</b>?
                </AppText>
              )}

              {!archiveProducts &&
              selectedOption?.value === ECOM_PRODUCT_STATUS_ENUM.ACTIVE ? (
                <>
                  <AppText style={{ fontSize: 14 }}>
                    This will make the products <b>available for sale</b> and
                    show them on your store.
                  </AppText>
                  <AppText
                    style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}
                  >
                    Any products without pricing will be set to draft.
                  </AppText>
                </>
              ) : (
                <AppText style={{ fontSize: 14 }}>
                  This will make the products <b>unavailable for sale</b> and{' '}
                  <b>hide</b> them from your store.
                </AppText>
              )}
            </AppStack>
          </Alert>
        </AppStack>
      </AppStack>
    </ResourceSelectModal>
  );
};

ChangeProductStatusModal.propTypes = {
  archiveProducts: PropTypes.bool,
  filter: PropTypes.object,
  fkEcomStore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fkEcomVendor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  selectAllDisabled: PropTypes.bool,
  selectFirstItem: PropTypes.bool,
  selectedProductIds: PropTypes.array,
  totalItemCount: PropTypes.number
};

export default ChangeProductStatusModal;
